import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface MarketingCtaBandProps {
  heading: string;
  subheading?: string;
  primaryLabel: string;
  primaryTo: string;
  secondaryLabel?: string;
  secondaryTo?: string;
}

/**
 * The inverted closing CTA band shared by the marketing pages. Lifts the band
 * that SaasFeatures / SaasCaseStudies inline so the tenant pages read as their
 * siblings. Uses the fixed (non-flipping) `cta` tokens so it stays
 * dark-with-light-text in both light and dark mode. All copy is passed in by
 * the caller (tenant DB content or i18n fallbacks).
 */
export default function MarketingCtaBand({
  heading,
  subheading,
  primaryLabel,
  primaryTo,
  secondaryLabel,
  secondaryTo,
}: MarketingCtaBandProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-cta to-cta-deep">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full bg-sage-700/40 blur-3xl"
      />
      <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl font-bold text-white">{heading}</h2>
        {subheading && <p className="mt-4 text-lg text-cta-fg/80">{subheading}</p>}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            to={primaryTo}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-cta-fg px-8 py-4 text-base font-semibold text-cta-deep transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-cta"
          >
            {primaryLabel}
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
          {secondaryLabel && secondaryTo && (
            <Link
              to={secondaryTo}
              className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-white/40 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {secondaryLabel}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
